import type { CSSProperties, ReactNode } from 'react'
import { Icon } from './Icon'

/** Buttons, fields and switches in the mockup's shapes. */

type Variant = 'accent' | 'outline' | 'ghost' | 'quiet' | 'danger'

export function Button({
  children,
  variant = 'ghost',
  size,
  onClick,
  disabled,
  title,
  type = 'button',
  style,
}: {
  children: ReactNode
  variant?: Variant
  size?: 'sm'
  onClick?: () => void
  disabled?: boolean
  title?: string
  type?: 'button' | 'submit'
  style?: CSSProperties
}) {
  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`fx-btn fx-btn--${variant}${size === 'sm' ? ' fx-btn--sm' : ''}`}
      style={style}
    >
      {children}
    </button>
  )
}

export function IconButton({
  children,
  onClick,
  title,
  variant,
  size,
  disabled,
}: {
  children: ReactNode
  onClick?: () => void
  title: string
  variant?: 'danger' | 'onTile'
  size?: 'sm'
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.()
      }}
      className={[
        'fx-icon-btn',
        variant ? `fx-icon-btn--${variant}` : '',
        size === 'sm' ? 'fx-icon-btn--sm' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </button>
  )
}

export function SearchField({
  value,
  onChange,
  placeholder,
  style,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  style?: CSSProperties
}) {
  return (
    <div className="fx-search" style={{ flex: 1, minWidth: 240, ...style }}>
      <Icon name="search" size={15} style={{ color: 'var(--faint)' }} />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  minWidth,
  height,
  ariaLabel,
}: {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
  minWidth?: number
  height?: number
  ariaLabel?: string
}) {
  return (
    <select
      className="fx-select"
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      style={{ minWidth, height }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
}) {
  return (
    <div className="fx-segment">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * A button that reads as on/off — the mockup's "Tax details" and
 * "Felder bearbeiten" controls, which fill with the accent when active.
 */
export function ToggleButton({
  active,
  onClick,
  children,
  height = 38,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
  height?: number
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className="fx-btn"
      style={{
        height,
        padding: '0 14px',
        color: active ? 'var(--onAccent)' : 'var(--muted)',
        background: active ? 'var(--accent)' : 'transparent',
        borderColor: active ? 'var(--accent)' : 'var(--line)',
      }}
    >
      {children}
    </button>
  )
}

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
}: {
  value: T
  onChange: (value: T) => void
  tabs: Array<{ value: T; label: string; count?: number }>
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {tabs.map((t) => {
        const active = t.value === value
        return (
          <button
            key={t.value}
            role="tab"
            type="button"
            aria-selected={active}
            className="fx-tab"
            onClick={() => onChange(t.value)}
          >
            {t.label}
            {t.count != null && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 999,
                  background: active ? 'var(--accent)' : 'var(--line2)',
                  color: active ? 'var(--onAccent)' : 'var(--muted)',
                }}
              >
                {t.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
